import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabView, TabPanel } from "primereact/tabview";
import { useAuth } from "../../../AuthContext";
import {
  FaFileAlt,
  FaEdit,
  FaTrash,
  FaPlus,
  FaFileExcel,
  FaChevronDown,
  FaChevronUp,
  FaEye,
  FaUserTie,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaClock,
  FaVoteYea,
  FaFilePdf,
  FaLink,
} from "react-icons/fa";

import "./MateriasLegislativas.css";

const API_BASE_URL = "https://sapl.cantarellisolucoes.com.br/wp-json/custom/v1";

const MateriasLegislativas = () => {
  const { user } = useAuth();
  const toast = useRef(null);

  // Estados
  const [loading, setLoading] = useState(false);
  const [materias, setMaterias] = useState([]);
  const [parlamentares, setParlamentares] = useState([]);
  const [legislaturas, setLegislaturas] = useState([]);
  const [legislaturaSelecionada, setLegislaturaSelecionada] = useState(null);
  const [tiposMateria, setTiposMateria] = useState([]);
  const [statusMateria, setStatusMateria] = useState([]);
  const [resultadosVotacao, setResultadosVotacao] = useState([]);

  // Estados de filtro
  const [termoPesquisa, setTermoPesquisa] = useState("");
  const [tipoSelecionado, setTipoSelecionado] = useState(null);
  const [statusSelecionado, setStatusSelecionado] = useState(null);
  const [anoSelecionado, setAnoSelecionado] = useState(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  // Estados do modal
  const [modalVisible, setModalVisible] = useState(false);
  const [editandoMateria, setEditandoMateria] = useState(null);
  const [formData, setFormData] = useState({
    tipo: "",
    numero: "",
    ano: new Date().getFullYear(),
    ementa: "",
    descricao: "",
    autor_id: null,
    status: "em_tramitacao",
    data_apresentacao: new Date(),
    data_ultima_tramitacao: null,
    data_votacao: null,
    resultado: "",
    data_fim_prazo: null,
    localizacao_atual: "",
    texto_original: "",
    texto_articulado: "",
    norma_vinculada: "",
    protocolo: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Modal de visualização
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [materiaSelecionada, setMateriaSelecionada] = useState(null);

  // Anos para dropdown
  const anos = Array.from({ length: 10 }, (_, i) => {
    const ano = new Date().getFullYear() - 5 + i; // Começa 5 anos atrás
    return { label: ano.toString(), value: ano };
  });

  // Função genérica para requisições fetch com token
  const apiRequest = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${user?.token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
        ...options.headers,
      },
      credentials: "include",
    });

    const data = await response.json();

    if (!response.ok) {
      throw { status: response.status, data };
    }

    return data;
  };

  // Carregar dados de configuração
  const carregarConfiguracoes = useCallback(async () => {
    try {
      const [tiposRes, statusRes, resultadosRes] = await Promise.all([
        apiRequest("/tipos-materia"),
        apiRequest("/status-materia"),
        apiRequest("/resultados-votacao"),
      ]);

      setTiposMateria(tiposRes.data || []);
      setStatusMateria(statusRes.data || []);
      setResultadosVotacao(resultadosRes.data || []);
    } catch (error) {
      console.error("Erro ao carregar configurações:", error);
    }
  }, [user?.token]);

  // Carregar legislaturas da API
  const carregarLegislaturas = useCallback(async () => {
    try {
      const response = await apiRequest("/legislaturas");
      const legislaturasList = response.data.map((leg) => ({
        id: leg.id,
        name: leg.descricao,
        code: leg.numero,
        atual: leg.ativa === 1,
        data_inicio: leg.data_inicio,
        data_fim: leg.data_fim,
      }));
      setLegislaturas(legislaturasList);

      const atual = legislaturasList.find((l) => l.atual);
      if (atual) setLegislaturaSelecionada(atual);
      else if (legislaturasList[0])
        setLegislaturaSelecionada(legislaturasList[0]);
    } catch (error) {
      console.error("Erro ao carregar legislaturas:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Não foi possível carregar as legislaturas",
        life: 5000,
      });
    }
  }, [user?.token]);

  // Carregar parlamentares
  const carregarParlamentares = useCallback(async () => {
    try {
      let url = "/parlamentares?ativo=true";
      if (legislaturaSelecionada?.id) {
        url += `&legislatura_id=${legislaturaSelecionada.id}`;
      }
      const response = await apiRequest(url);
      setParlamentares(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar parlamentares:", error);
    }
  }, [user?.token, legislaturaSelecionada]);

  // Carregar matérias
  const carregarMaterias = useCallback(async () => {
    setLoading(true);
    try {
      let url = "/materias";
      const params = new URLSearchParams();

      if (termoPesquisa) params.append("search", termoPesquisa);
      if (tipoSelecionado) params.append("tipo", tipoSelecionado);
      if (statusSelecionado) params.append("status", statusSelecionado);
      if (anoSelecionado) params.append("ano", anoSelecionado);
      if (legislaturaSelecionada?.id)
        params.append("legislatura_id", legislaturaSelecionada.id);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await apiRequest(url);
      setMaterias(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar matérias:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Não foi possível carregar as matérias legislativas",
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [
    user?.token,
    termoPesquisa,
    tipoSelecionado,
    statusSelecionado,
    anoSelecionado,
    legislaturaSelecionada,
  ]);

  // Efeitos
  useEffect(() => {
    if (user?.token) {
      carregarConfiguracoes();
      carregarLegislaturas();
    }
  }, [user?.token, carregarConfiguracoes, carregarLegislaturas]);

  useEffect(() => {
    if (user?.token && legislaturaSelecionada) {
      carregarParlamentares();
      carregarMaterias();
    }
  }, [
    user?.token,
    legislaturaSelecionada,
    carregarParlamentares,
    carregarMaterias,
  ]);

  // Abrir modal para criar
  const abrirModalCriar = () => {
    setEditandoMateria(null);
    setFormData({
      tipo: "",
      numero: "",
      ano: new Date().getFullYear(),
      ementa: "",
      descricao: "",
      autor_id: null,
      status: "em_tramitacao",
      data_apresentacao: new Date(),
      data_ultima_tramitacao: null,
      data_votacao: null,
      resultado: "",
      data_fim_prazo: null,
      localizacao_atual: "",
      texto_original: "",
      texto_articulado: "",
      norma_vinculada: "",
      protocolo: "",
    });
    setModalVisible(true);
  };

  // Abrir modal para editar
  const abrirModalEditar = (materia) => {
    // console.log("Matéria para editar:", materia); // Debug

    // Encontrar os objetos completos para os Dropdowns
    const tipoObj = tiposMateria.find((t) => t.valor === materia.tipo);
    const statusObj = statusMateria.find((s) => s.valor === materia.status);
    const resultadoObj = resultadosVotacao.find(
      (r) => r.valor === materia.resultado,
    );

    // Converter datas com segurança
    const parseDateSafe = (dateStr) => {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? null : date;
    };

    setEditandoMateria(materia);
    setFormData({
      tipo:
        tipoObj ||
        (materia.tipo ? { valor: materia.tipo, label: materia.tipo } : ""),
      numero: materia.numero || "",
      ano: materia.ano || new Date().getFullYear(),
      ementa: materia.ementa || "",
      descricao: materia.descricao || "",
      autor_id: materia.autor_id || null,
      status:
        statusObj ||
        (materia.status
          ? { valor: materia.status, label: materia.status }
          : ""),
      data_apresentacao: parseDateSafe(materia.data_apresentacao) || new Date(),
      data_ultima_tramitacao: parseDateSafe(materia.data_ultima_tramitacao),
      data_votacao: parseDateSafe(materia.data_votacao),
      resultado:
        resultadoObj ||
        (materia.resultado
          ? { valor: materia.resultado, label: materia.resultado }
          : ""),
      data_fim_prazo: parseDateSafe(materia.data_fim_prazo),
      localizacao_atual: materia.localizacao_atual || "",
      texto_original: materia.texto_original || "",
      texto_articulado: materia.texto_articulado || "",
      norma_vinculada: materia.norma_vinculada || "",
      protocolo: materia.protocolo || "",
    });
    setModalVisible(true);
  };

  // Abrir modal de visualização
  const abrirVisualizacao = (materia) => {
    setMateriaSelecionada(materia);
    setViewModalVisible(true);
  };

  // Salvar matéria
  const salvarMateria = async () => {
    if (!formData.tipo) {
      toast.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "O tipo da matéria é obrigatório",
        life: 3000,
      });
      return;
    }

    if (!formData.numero) {
      toast.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "O número da matéria é obrigatório",
        life: 3000,
      });
      return;
    }

    if (!formData.ementa.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "A ementa é obrigatória",
        life: 3000,
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        tipo: formData.tipo?.valor || formData.tipo,
        numero: parseInt(formData.numero),
        ano: parseInt(formData.ano),
        ementa: formData.ementa,
        descricao: formData.descricao,
        autor_id: formData.autor_id || null,
        status: formData.status?.valor || formData.status,
        data_apresentacao: formData.data_apresentacao
          ? formData.data_apresentacao.toISOString().split("T")[0]
          : null,
        data_ultima_tramitacao: formData.data_ultima_tramitacao
          ? formData.data_ultima_tramitacao.toISOString().split("T")[0]
          : null,
        data_votacao: formData.data_votacao
          ? formData.data_votacao.toISOString().split("T")[0]
          : null,
        resultado: formData.resultado?.valor || formData.resultado || "",
        data_fim_prazo: formData.data_fim_prazo
          ? formData.data_fim_prazo.toISOString().split("T")[0]
          : null,
        localizacao_atual: formData.localizacao_atual,
        texto_original: formData.texto_original,
        texto_articulado: formData.texto_articulado,
        norma_vinculada: formData.norma_vinculada,
        protocolo: formData.protocolo,
        legislatura_id: legislaturaSelecionada?.id,
      };

      // ADICIONE ESTE LOG
      console.log("Payload sendo enviado:", JSON.stringify(payload, null, 2));

      if (editandoMateria) {
        await apiRequest(`/materias/${editandoMateria.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Matéria atualizada com sucesso",
          life: 3000,
        });
      } else {
        await apiRequest("/materias", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Matéria criada com sucesso",
          life: 3000,
        });
      }

      setModalVisible(false);
      carregarMaterias();
    } catch (error) {
      console.error("Erro ao salvar matéria:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: error.data?.message || "Falha ao salvar matéria",
        life: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Excluir matéria
  const confirmarExclusao = (materia) => {
    const tipoLabel =
      tiposMateria.find((t) => t.value === materia.tipo)?.label || materia.tipo;

    confirmDialog({
      header: "",
      style: { width: "350px" },
      className: "custom-confirm-dialog",

      message: (
        <div className="flex flex-column align-items-center text-center">
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: "3rem", color: "#f59e0b", marginBottom: "1rem" }}
          ></i>

          <span
            style={{
              fontSize: "1.1rem",
              fontWeight: "500",
              marginBottom: "0.5rem",
            }}
          >
            Confirmar exclusão
          </span>

          <span style={{ fontSize: "0.95rem", color: "#6b7280" }}>
            Deseja realmente <strong>excluir</strong> a matéria
          </span>

          <span
            style={{ fontSize: "1rem", fontWeight: "600", marginTop: "0.5rem" }}
          >
            {tipoLabel} nº {materia.numero}/{materia.ano}?
          </span>
        </div>
      ),

      footer: (options) => (
        <div className="flex justify-content-center gap-2 w-full">
          <button
            className="p-button p-component p-button-sm p-button-secondary"
            onClick={options.reject}
          >
            <span className="p-button-label">Cancelar</span>
          </button>

          <button
            className="p-button p-component p-button-sm p-button-danger"
            onClick={options.accept}
          >
            <span className="p-button-label">Excluir</span>
          </button>
        </div>
      ),

      accept: async () => {
        try {
          await apiRequest(`/materias/${materia.id}`, { method: "DELETE" });
          toast.current?.show({
            severity: "success",
            summary: "Excluído",
            detail: "Matéria removida com sucesso",
            life: 3000,
          });
          carregarMaterias();
        } catch (error) {
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: error.data?.message || "Falha ao excluir matéria",
            life: 5000,
          });
        }
      },
    });
  };

  // Templates da tabela
  const tipoBody = (rowData) => {
    const tipo = tiposMateria.find((t) => t.value === rowData.tipo);
    return <Tag value={tipo?.label || rowData.tipo} severity="info" />;
  };

  const numeroBody = (rowData) => (
    <span style={{ fontSize: "0.9rem", fontWeight: "600" }}>
      {rowData.numero}/{rowData.ano}
    </span>
  );

  const ementaBody = (rowData) => (
    <div style={{ fontSize: "0.9rem", lineHeight: "1.4" }}>
      {rowData.ementa?.substring(0, 100)}
      {rowData.ementa?.length > 100 && "..."}
    </div>
  );

  const autorBody = (rowData) => (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <div
        style={{
          width: "32px", // Reduzido de 40px
          height: "32px", // Reduzido de 40px
          borderRadius: "50%",
          backgroundColor: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#3b82f6",
          fontSize: "0.9rem", // Reduzido
        }}
      >
        <FaUserTie size={14} /> {/* Tamanho fixo */}
      </div>
      <div>
        <div style={{ fontSize: "0.9rem", fontWeight: "500" }}>
          {rowData.autor_nome || "Não informado"}
        </div>
      </div>
    </div>
  );

  const statusBody = (rowData) => {
    const status = statusMateria.find((s) => s.value === rowData.status);
    return (
      <Tag
        value={status?.label || rowData.status}
        severity={status?.severity || "secondary"}
      />
    );
  };

  const localizacaoBody = (rowData) => (
    <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>
      {rowData.localizacao_atual || "---"}
    </span>
  );

  const acoesBody = (rowData) => (
    <React.Fragment>
      <Button
        icon="pi pi-eye"
        size="small"
        severity="info"
        tooltip="Visualizar"
        tooltipOptions={{ position: "left" }}
        className="mr-2 custom-button button-efeito"
        style={{ width: "32px", height: "32px" }}
        onClick={() => abrirVisualizacao(rowData)}
      />
      <Button
        icon="pi pi-file-edit"
        size="small"
        severity="success"
        tooltip="Editar"
        tooltipOptions={{ position: "left" }}
        className="mr-2 custom-button button-efeito"
        style={{ width: "32px", height: "32px" }}
        onClick={() => abrirModalEditar(rowData)}
      />
      <Button
        icon="pi pi-trash"
        size="small"
        severity="danger"
        tooltip="Excluir"
        tooltipOptions={{ position: "left" }}
        className="custom-button button-efeito"
        style={{ width: "32px", height: "32px" }}
        onClick={() => confirmarExclusao(rowData)}
      />
    </React.Fragment>
  );

  const emptyMessage = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 20px",
        color: "#6b7280",
      }}
    >
      <FaFileAlt size={48} style={{ color: "#9ca3af", marginBottom: "20px" }} />
      <h3 style={{ marginBottom: "8px", color: "#374151", fontWeight: "500" }}>
        Nenhuma matéria encontrada
      </h3>
      <p
        style={{
          textAlign: "center",
          maxWidth: "400px",
          fontSize: "0.85rem",
          lineHeight: "1.5",
        }}
      >
        Não há matérias legislativas para a legislatura selecionada.
        <br />
        Clique em "Adicionar" para criar uma nova matéria.
      </p>
    </div>
  );

  const modalFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancelar"
        size="small"
        severity="secondary"
        icon="pi pi-times"
        onClick={() => setModalVisible(false)}
        outlined
      />
      <Button
        label="Salvar"
        size="small"
        severity="success"
        icon="pi pi-check"
        onClick={salvarMateria}
        loading={submitting}
        autoFocus
      />
    </div>
  );

  const totalMaterias = materias.length;

  return (
    <div className="dashboard-container">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="dashboard-header">
        <h1>Matérias Legislativas</h1>
        <p>
          Gestão de projetos de lei, requerimentos, indicações e outras matérias
        </p>
      </div>

      <div className="page-content">
        {/* Seção de escolha da legislatura */}
        <Card style={{ padding: "0", marginBottom: "10px" }}>
          <div style={{ padding: "0 10px" }}>
            <h2 style={{ margin: 0, color: "#374151" }}>Selecione o Período</h2>
          </div>

          <div style={{ padding: "0 10px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "3fr 1fr",
                gap: "24px",
                alignItems: "end",
              }}
            >
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <label
                  htmlFor="legislatura-dropdown"
                  style={{
                    fontWeight: "500",
                    color: "#374151",
                    fontSize: "0.95rem",
                  }}
                >
                  Escolha uma Legislatura
                </label>
                <Dropdown
                  value={legislaturaSelecionada}
                  options={legislaturas}
                  onChange={(e) => setLegislaturaSelecionada(e.value)}
                  optionLabel="name"
                  placeholder="Selecione uma legislatura"
                  loading={!legislaturas.length}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  alignItems: "flex-start",
                }}
              >
                <label
                  style={{
                    fontWeight: "500",
                    color: "#374151",
                    fontSize: "0.95rem",
                  }}
                >
                  Pesquisar Matéria
                </label>
                <Button
                  label={mostrarFiltros ? "Ocultar Filtros" : "Mostrar Filtros"}
                  icon={mostrarFiltros ? <FaChevronUp /> : <FaChevronDown />}
                  severity="secondary"
                  outlined
                  onClick={() => setMostrarFiltros(!mostrarFiltros)}
                  style={{ minWidth: "200px" }}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Filtros de busca */}
        {mostrarFiltros && (
          <Card style={{ padding: "0", marginBottom: "10px" }}>
            <div style={{ padding: "0 10px" }}>
              <h2 style={{ margin: 0, color: "#374151" }}>Filtros</h2>
            </div>

            <div style={{ padding: "0 10px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: "24px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <label
                    style={{
                      fontWeight: "500",
                      color: "#374151",
                      fontSize: "0.95rem",
                    }}
                  >
                    Buscar por texto
                  </label>
                  <InputText
                    value={termoPesquisa}
                    onChange={(e) => setTermoPesquisa(e.target.value)}
                    placeholder="Número, ementa ou autor..."
                    className="pesquisa-input"
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <label
                    style={{
                      fontWeight: "500",
                      color: "#374151",
                      fontSize: "0.95rem",
                    }}
                  >
                    Tipo
                  </label>
                  <Dropdown
                    value={tipoSelecionado}
                    options={tiposMateria}
                    onChange={(e) => setTipoSelecionado(e.value)}
                    optionLabel="label"
                    optionValue="valor"
                    placeholder="Todos"
                    showClear
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <label
                    style={{
                      fontWeight: "500",
                      color: "#374151",
                      fontSize: "0.95rem",
                    }}
                  >
                    Status
                  </label>
                  <Dropdown
                    value={statusSelecionado}
                    options={statusMateria}
                    onChange={(e) => setStatusSelecionado(e.value)}
                    optionLabel="label"
                    optionValue="valor"
                    placeholder="Todos"
                    showClear
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <label
                    style={{
                      fontWeight: "500",
                      color: "#374151",
                      fontSize: "0.95rem",
                    }}
                  >
                    Ano
                  </label>
                  <Dropdown
                    value={anoSelecionado}
                    options={anos}
                    onChange={(e) => setAnoSelecionado(e.value)}
                    optionLabel="label"
                    placeholder="Todos"
                    showClear
                  />
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Tabela de matérias */}
        <Card style={{ padding: "0" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              padding: "10px",
            }}
          >
            <Button
              label="Adicionar"
              icon="pi pi-plus"
              severity="success"
              size="small"
              onClick={abrirModalCriar}
              disabled={loading}
            />
            <Button
              label="Exportar"
              icon="pi pi-file-excel"
              severity="secondary"
              size="small"
              outlined
              onClick={() => console.log("Exportar...")}
            />
          </div>

          <div style={{ padding: "0 0 10px 0", width: "100%" }}>
            <Message
              severity="info"
              text={`Legislatura: ${legislaturaSelecionada?.name || "Selecione uma legislatura"} ${legislaturaSelecionada?.atual ? "(Atual)" : ""} • Total de Matérias: ${totalMaterias}`}
              style={{ width: "100%" }}
            />
          </div>

          {loading ? (
            <div className="loading-container">
              <ProgressSpinner />
            </div>
          ) : (
            <DataTable
              value={materias}
              dataKey="id"
              style={{ width: "100%" }}
              paginator
              rows={10}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate="{first} a {last} de {totalRecords}"
              emptyMessage={emptyMessage}
              showGridlines
              stripedRows
            >
              <Column
                field="tipo"
                header="Tipo"
                body={tipoBody}
                sortable
                style={{ width: "40px" }}
              />
              <Column
                header="Nº/Ano"
                body={numeroBody}
                sortable
                style={{ width: "80px" }}
              />
              <Column
                header="Ementa"
                body={ementaBody}
                sortable
                style={{ width: "300px" }}
              />
              <Column
                header="Autor"
                body={autorBody}
                sortable
                style={{ width: "300px" }}
              />
              <Column
                header="Status"
                body={statusBody}
                sortable
                style={{ width: "80px" }}
              />
              <Column
                header="Localização"
                body={localizacaoBody}
                sortable
                style={{ width: "220px" }}
              />
              <Column
                header="Ações"
                body={acoesBody}
                style={{ width: "170px" }}
              />
            </DataTable>
          )}
        </Card>
      </div>

      {/* Modal de Criar/Editar Matéria */}
      <Dialog
        visible={modalVisible}
        style={{ width: "950px", maxWidth: "90vw" }}
        modal
        className="p-fluid modern-dialog"
        onHide={() => setModalVisible(false)}
        footer={modalFooter}
        header={
          <div className="flex align-items-center gap-2">
            <div
              className="bg-primary bg-opacity-10 border-circle p-2"
              style={{ borderRadius: "12px" }}
            >
              <i
                className={`pi ${editandoMateria ? "pi-file-edit" : "pi-file"} text-primary text-xl`}
              ></i>
            </div>
            <span className="font-bold text-xl">
              {editandoMateria ? "Editar Matéria" : "Nova Matéria"}
            </span>
          </div>
        }
      >
        <TabView className="tabview-colada">
          <TabPanel header="Dados Básicos" headerClassName="tab-header-colada">
            <div
              className="grid p-fluid"
              style={{
                backgroundColor: "#fafafa",
                border: "1px solid #e8e8e8",
                borderTop: "none",
                padding: "15px",
                borderRadius: "0 0 12px 12px",
                margin: 0,
              }}
            >
              <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Tipo *</label>
                  <Dropdown
                    value={formData.tipo}
                    options={tiposMateria}
                    onChange={(e) =>
                      setFormData({ ...formData, tipo: e.value })
                    }
                    optionLabel="label"
                    //optionValue="valor"
                    placeholder="Selecione o tipo"
                  />
                </div>
              </div>

              <div className="col-12 md:col-3">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Número *</label>
                  <InputText
                    value={formData.numero}
                    onChange={(e) =>
                      setFormData({ ...formData, numero: e.target.value })
                    }
                    placeholder="Número"
                  />
                </div>
              </div>

              <div className="col-12 md:col-3">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Ano</label>
                  <Dropdown
                    value={formData.ano}
                    options={anos}
                    onChange={(e) => setFormData({ ...formData, ano: e.value })}
                    optionLabel="label"
                    placeholder="Ano"
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Ementa *</label>
                  <InputTextarea
                    rows={3}
                    value={formData.ementa}
                    onChange={(e) =>
                      setFormData({ ...formData, ementa: e.target.value })
                    }
                    placeholder="Breve resumo do conteúdo da matéria"
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Descrição</label>
                  <InputTextarea
                    rows={3}
                    value={formData.descricao}
                    onChange={(e) =>
                      setFormData({ ...formData, descricao: e.target.value })
                    }
                    placeholder="Descrição detalhada da matéria (opcional)"
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Autor</label>
                  <Dropdown
                    value={formData.autor_id}
                    options={parlamentares.map((p) => ({
                      label: `${p.nome}${p.nome_parlamentar ? ` (${p.nome_parlamentar})` : ""} - ${p.partido || "Sem partido"}`,
                      value: p.id,
                    }))}
                    onChange={(e) =>
                      setFormData({ ...formData, autor_id: e.value })
                    }
                    placeholder="Selecione o autor"
                    filter
                    showClear
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Status</label>
                  <Dropdown
                    value={formData.status}
                    options={statusMateria}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.value })
                    }
                    optionLabel="label"
                    //optionValue="valor"
                    placeholder="Selecione o status"
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Protocolo</label>
                  <InputText
                    value={formData.protocolo}
                    onChange={(e) =>
                      setFormData({ ...formData, protocolo: e.target.value })
                    }
                    placeholder="Número do protocolo"
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">
                    Localização Atual
                  </label>
                  <InputText
                    value={formData.localizacao_atual}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        localizacao_atual: e.target.value,
                      })
                    }
                    placeholder="Ex: Arquivo, Comissão, Plenário"
                  />
                </div>
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Datas e Prazos" headerClassName="tab-header-colada">
            <div
              className="grid p-fluid"
              style={{
                backgroundColor: "#fafafa",
                border: "1px solid #e8e8e8",
                borderTop: "none",
                padding: "15px",
                borderRadius: "0 0 12px 12px",
                margin: 0,
              }}
            >
              <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">
                    Data de Apresentação
                  </label>
                  <Calendar
                    value={formData.data_apresentacao}
                    onChange={(e) =>
                      setFormData({ ...formData, data_apresentacao: e.value })
                    }
                    dateFormat="dd/mm/yy"
                    placeholder="dd/mm/aaaa"
                    showIcon
                    showButtonBar
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">
                    Data da Última Tramitação
                  </label>
                  <Calendar
                    value={formData.data_ultima_tramitacao}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        data_ultima_tramitacao: e.value,
                      })
                    }
                    dateFormat="dd/mm/yy"
                    placeholder="dd/mm/aaaa"
                    showIcon
                    showButtonBar
                    showClear
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Data de Votação</label>
                  <Calendar
                    value={formData.data_votacao}
                    onChange={(e) =>
                      setFormData({ ...formData, data_votacao: e.value })
                    }
                    dateFormat="dd/mm/yy"
                    placeholder="dd/mm/aaaa"
                    showIcon
                    showButtonBar
                    showClear
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">
                    Data Fim do Prazo
                  </label>
                  <Calendar
                    value={formData.data_fim_prazo}
                    onChange={(e) =>
                      setFormData({ ...formData, data_fim_prazo: e.value })
                    }
                    dateFormat="dd/mm/yy"
                    placeholder="dd/mm/aaaa"
                    showIcon
                    showButtonBar
                    showClear
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">
                    Resultado da Votação
                  </label>
                  <Dropdown
                    value={formData.resultado}
                    options={resultadosVotacao}
                    onChange={(e) =>
                      setFormData({ ...formData, resultado: e.value })
                    }
                    optionLabel="label"
                    //optionValue="valor"
                    placeholder="Selecione o resultado"
                    showClear
                  />
                </div>
              </div>
            </div>
          </TabPanel>

          <TabPanel
            header="Textos e Documentos"
            headerClassName="tab-header-colada"
          >
            <div
              className="grid p-fluid"
              style={{
                backgroundColor: "#fafafa",
                border: "1px solid #e8e8e8",
                borderTop: "none",
                padding: "15px",
                borderRadius: "0 0 12px 12px",
                margin: 0,
              }}
            >
              <div className="col-12">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Texto Original</label>
                  <InputTextarea
                    rows={6}
                    value={formData.texto_original}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        texto_original: e.target.value,
                      })
                    }
                    placeholder="Cole aqui o texto original da matéria..."
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Texto Articulado</label>
                  <InputTextarea
                    rows={6}
                    value={formData.texto_articulado}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        texto_articulado: e.target.value,
                      })
                    }
                    placeholder="Texto com artigos, parágrafos, incisos..."
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">
                    Norma Jurídica Vinculada
                  </label>
                  <InputTextarea
                    rows={3}
                    value={formData.norma_vinculada}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        norma_vinculada: e.target.value,
                      })
                    }
                    placeholder="Ex: Lei Ordinária nº 304, de 15 de abril de 2026"
                  />
                </div>
              </div>
            </div>
          </TabPanel>
        </TabView>
      </Dialog>

      {/* Modal de Visualização - Estilo SAPL */}
      <Dialog
        visible={viewModalVisible}
        style={{ width: "950px", maxWidth: "90vw" }}
        modal
        className="p-fluid modern-dialog"
        onHide={() => setViewModalVisible(false)}
        header={
          <div className="flex align-items-center gap-2">
            <div
              className="bg-primary bg-opacity-10 border-circle p-2"
              style={{ borderRadius: "12px" }}
            >
              <i className="pi pi-info-circle text-primary text-xl"></i>
            </div>
            <span className="font-bold text-xl">Detalhes da Matéria</span>
          </div>
        }
        footer={
          <div className="flex justify-content-between gap-2">
            <Button
              label="Fechar"
              size="small"
              severity="secondary"
              icon="pi pi-times"
              onClick={() => setViewModalVisible(false)}
              outlined
            />
            <Button
              label="Editar"
              size="small"
              severity="success"
              icon="pi pi-pencil"
              onClick={() => {
                setViewModalVisible(false);
                abrirModalEditar(materiaSelecionada);
              }}
            />
          </div>
        }
      >
        {materiaSelecionada && (
          <div style={{ padding: "0" }}>
            {/* Cabeçalho com tipo e número */}
            <div
              style={{
                backgroundColor: "#f8f9fa",
                borderBottom: "2px solid #3b82f6",
                padding: "15px",
                borderRadius: "8px 8px 0 0",
                marginBottom: "15px",
              }}
            >
              <div className="flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  <h2 style={{ margin: 0, color: "#1e293b" }}>
                    {tiposMateria.find(
                      (t) => t.value === materiaSelecionada.tipo,
                    )?.label || materiaSelecionada.tipo}{" "}
                    {materiaSelecionada.numero}/{materiaSelecionada.ano}
                  </h2>
                  {materiaSelecionada.localizacao_atual && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        fontSize: "0.85rem",
                        color: "#64748b",
                      }}
                    >
                      <span>{materiaSelecionada.localizacao_atual}</span>
                    </div>
                  )}
                  {materiaSelecionada.ementa && (
                    <p
                      style={{
                        margin: "8px 0 0 0",
                        color: "#475569",
                        fontSize: "0.9rem",
                      }}
                    >
                      {materiaSelecionada.ementa}
                    </p>
                  )}
                </div>
                <div className="flex flex-column align-items-end gap-2">
                  {(() => {
                    const status = statusMateria.find(
                      (s) => s.value === materiaSelecionada.status,
                    );
                    return (
                      <Tag
                        value={status?.label || materiaSelecionada.status}
                        severity={status?.severity || "secondary"}
                      />
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Informações do Autor */}
            <div style={{ padding: "0 15px", marginBottom: "15px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "10px",
                  backgroundColor: "#f1f5f9",
                  borderRadius: "8px",
                }}
              >
                <FaUserTie size={20} style={{ color: "#3b82f6" }} />
                <div>
                  <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                    Autor
                  </div>
                  <div style={{ fontWeight: "600", color: "#1e293b" }}>
                    {materiaSelecionada.autor_nome || "Não informado"}
                    {materiaSelecionada.autor_partido &&
                      ` (${materiaSelecionada.autor_partido})`}
                  </div>
                </div>
              </div>
            </div>

            {/* Grid de informações */}
            <div style={{ padding: "0 15px", marginBottom: "15px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "15px",
                }}
              >
                {materiaSelecionada.data_apresentacao && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FaCalendarAlt style={{ color: "#64748b" }} />
                    <div>
                      <div style={{ fontSize: "0.7rem", color: "#64748b" }}>
                        Apresentação
                      </div>
                      <div style={{ fontSize: "0.9rem", fontWeight: "500" }}>
                        {new Date(
                          materiaSelecionada.data_apresentacao,
                        ).toLocaleDateString("pt-BR")}
                      </div>
                    </div>
                  </div>
                )}

                {materiaSelecionada.data_ultima_tramitacao && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FaClock style={{ color: "#64748b" }} />
                    <div>
                      <div style={{ fontSize: "0.7rem", color: "#64748b" }}>
                        Última Tramitação
                      </div>
                      <div style={{ fontSize: "0.9rem", fontWeight: "500" }}>
                        {new Date(
                          materiaSelecionada.data_ultima_tramitacao,
                        ).toLocaleDateString("pt-BR")}
                      </div>
                    </div>
                  </div>
                )}

                {materiaSelecionada.data_fim_prazo && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FaClock style={{ color: "#64748b" }} />
                    <div>
                      <div style={{ fontSize: "0.7rem", color: "#64748b" }}>
                        Fim do Prazo
                      </div>
                      <div style={{ fontSize: "0.9rem", fontWeight: "500" }}>
                        {new Date(
                          materiaSelecionada.data_fim_prazo,
                        ).toLocaleDateString("pt-BR")}
                      </div>
                    </div>
                  </div>
                )}

                {materiaSelecionada.data_votacao && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FaVoteYea style={{ color: "#64748b" }} />
                    <div>
                      <div style={{ fontSize: "0.7rem", color: "#64748b" }}>
                        Data da Votação
                      </div>
                      <div style={{ fontSize: "0.9rem", fontWeight: "500" }}>
                        {new Date(
                          materiaSelecionada.data_votacao,
                        ).toLocaleDateString("pt-BR")}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Resultado da Votação */}
            {materiaSelecionada.resultado && (
              <div style={{ padding: "0 15px", marginBottom: "15px" }}>
                <div
                  style={{
                    padding: "10px",
                    backgroundColor: "#fef3c7",
                    borderRadius: "8px",
                    borderLeft: "4px solid #f59e0b",
                  }}
                >
                  <div style={{ fontSize: "0.8rem", color: "#92400e" }}>
                    Resultado da Votação
                  </div>
                  <div style={{ fontWeight: "600", color: "#78350f" }}>
                    {resultadosVotacao.find(
                      (r) => r.value === materiaSelecionada.resultado,
                    )?.label || materiaSelecionada.resultado}
                  </div>
                </div>
              </div>
            )}

            {/* Descrição */}
            {materiaSelecionada.descricao && (
              <div style={{ padding: "0 15px", marginBottom: "15px" }}>
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#64748b",
                    marginBottom: "5px",
                  }}
                >
                  Descrição
                </div>
                <div style={{ lineHeight: "1.5", color: "#334155" }}>
                  {materiaSelecionada.descricao}
                </div>
              </div>
            )}

            {/* Norma Vinculada */}
            {materiaSelecionada.norma_vinculada && (
              <div style={{ padding: "0 15px", marginBottom: "15px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px",
                    backgroundColor: "#dcfce7",
                    borderRadius: "8px",
                  }}
                >
                  <FaLink style={{ color: "#16a34a" }} />
                  <div>
                    <div style={{ fontSize: "0.7rem", color: "#166534" }}>
                      Norma Jurídica Vinculada
                    </div>
                    <div style={{ fontWeight: "500", color: "#14532d" }}>
                      {materiaSelecionada.norma_vinculada}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Documentos Acessórios */}
            <div style={{ padding: "0 15px", marginBottom: "15px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px",
                  backgroundColor: "#e0e7ff",
                  borderRadius: "8px",
                }}
              >
                <FaFilePdf style={{ color: "#3b82f6" }} />
                <div>
                  <div style={{ fontSize: "0.7rem", color: "#1e40af" }}>
                    Documentos Acessórios
                  </div>
                  <div style={{ fontWeight: "500", color: "#1e3a8a" }}>
                    {materiaSelecionada.documentos_acessorios || 0} documento(s)
                    anexado(s)
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default MateriasLegislativas;
